<template>
  <header
    v-if="variants.v1 === variant"
    :class="['header', '-type-1', 'js-header', { scrolled: isScrolled, 'is-hidden': isPopupOpen }]"
    :style="headerStyle"
  >
    <div data-anim="fade delay-3" class="header__container tw-items-center">
      <div class="header__left">
        <button class="header__menuBtn js-menu-button" @click="toggleMenu">
          <i></i>
          <span>MENU</span>
        </button>
        <nuxt-link :to="homeLink" class="header__logo" :external="true">
          <img :src="logo" alt="logo icon" class="logo-image" />
        </nuxt-link>
      </div>
      <div class="header__right">
        <div class="header__socials">
          <a
            v-for="(item, i) in vars.socials as SocialItem[]"
            :key="`social-${i}`"
            :href="item.url"
            :class="item.iconClass"
            target="_blank"
            rel="noopener noreferrer"
          ></a>
        </div>
        <div class="header__language lg:d-none">
          <template v-for="(lang, idx) in vars.languages as string[]" :key="lang">
            <a href="javascript:void(0)" @click.prevent="changeLanguage(lang)">
              {{ lang.toUpperCase() }}
            </a>
            <span v-if="idx < (vars.languages as string[]).length - 1"></span>
          </template>
        </div>
        <div class="tw-flex tw-w-full tw-flex-wrap tw-justify-start tw-gap-3">
          <div class="tw-flex tw-gap-3">
            <nuxt-link
              v-for="(button, index) in buttons"
              :key="`button-${index}`"
              :class="[buttons.length >= 2 ? '' : 'tw-w-auto', `n-${button.button_type}`, 'tw-uppercase']"
              :to="button.button_link"
              :external="true"
            >
              {{ button.button_label }}
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>
  </header>
  <div
    v-if="variants.v1 === variant"
    class="menu -top-header js-menu"
    :class="{ 'is-open': menuVisible }"
    :style="menuStyle"
  >
    <div class="menu__left !tw-bg-white">
      <div class="menuNav js-menuNav">
        <div
          v-for="(menuItem, itemIndex) in columnOneMenu"
          :key="`menuItem-${itemIndex}`"
          class="menuNav__item"
          :class="menuItem.has_sub_menu ? '-has-submenu js-has-submenu' : ''"
        >
          <a
            href="javascript:void(0)"
            class="tw-group hover:!tw-text-[var(--c-secondary)]"
            @click="menuItem.has_sub_menu ? toggleSubMenu(itemIndex) : handleMenuLinkClick(menuItem)"
            :aria-expanded="openSubMenu === itemIndex"
          >
            <h4>{{ menuItem.name }}</h4>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <svg
              v-if="menuItem.has_sub_menu === true"
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14 8.49799H8V14.498H6V8.49799H0V6.49799H6V0.497986H8V6.49799H14V8.49799Z"
                fill="black"
                class="tw-duration-300 group-hover:tw-fill-[var(--c-secondary)]"
              />
            </svg>
          </a>
          <div
            class="submenu js-submenu"
            :class="{ 'is-active': openSubMenu === itemIndex }"
            role="region"
            :aria-hidden="openSubMenu !== itemIndex"
          >
            <div class="submenu__wrap">
              <div
                class="submenu__item"
                v-for="(subMenu, sIndex) in menuItem.sub_menus"
                :key="`subMenu-${itemIndex}-${sIndex}`"
              >
                <nuxt-link
                  :to="subMenu.url || '/'"
                  :target="subMenu.is_open_new_tab ? '_blank' : '_self'"
                  :external="true"
                  @click="toggleMenu"
                >
                  <h6>{{ subMenu.name }}</h6>
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="menu__right">
      <div class="">
        <NuxtImg :src="vars.backgroundImage" class="tw-absolute tw-top-0" />
      </div>
    </div>
    <div class="menu__close js-menuClose js-menu-button" @click="toggleMenu">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z"
          fill="#000"
        />
      </svg>
    </div>
  </div>
</template>

<script lang="ts">
import { defineNuxtComponent, navigateTo } from '#app';
import { mapState } from 'pinia';
import { MenuTypes, ViewTypes } from '~/constants/ui.constants';
import { ComponentMixin } from '~/mixins/component.mixin';
import { useWebsiteStore } from '~/units/website/store';
import { useCoreStore } from '~/units/core/store';

interface MenuItem {
  name: string;
  url: string;
  has_sub_menu: boolean;
  is_open_new_tab: boolean;
  sub_menus?: MenuItem[];
}

interface SocialItem {
  url: string;
  iconClass: string;
}

const getCssColorVar = (colorKeyOrValue: string | undefined | null): string => {
  if (!colorKeyOrValue) return '';
  if (colorKeyOrValue.startsWith('#') || colorKeyOrValue.startsWith('rgb') || colorKeyOrValue.startsWith('hsl'))
    return colorKeyOrValue;
  const cleanedKey = colorKeyOrValue.replace(/-/g, '').toLowerCase();
  return `var(--c-${cleanedKey})`;
};

export default defineNuxtComponent({
  name: 'Theme8BaseHeaderOne',
  mixins: [ComponentMixin],
  data() {
    return {
      menuVisible: false,
      isScrolled: false,
      openSubMenu: null as number | null,
      isPopupOpen: false, // New data property
      variants: { v1: 'v1', HEADER_2: 'HEADER_2' },
      scrollThreshold: 50,
    };
  },
  computed: {
    menu() {
      return this.component?.variables['menu'] || {};
    },
    buttons() {
      return this.groupedVariables.buttons;
    },
    ...mapState(useWebsiteStore, ['headerMenuItems', 'company']),
    ...mapState(useCoreStore, ['isAnythingOnTopVisible']),
    homeLink(): string {
      return '/';
    },
    logo(): string {
      const currentCompany = this.company as any;
      const defaultLogo = currentCompany?.logo || '';
      // Show the light logo only if the menu is open.
      if (this.menuVisible) {
        return currentCompany?.light_logo || defaultLogo;
      }
      return defaultLogo;
    },
    columnOneMenu(): MenuItem[] {
      const content = this.component?.variables['column_one_content-text'];
      if (content === MenuTypes.HEADER_MENU) {
        return (this.headerMenuItems as MenuItem[]) || [];
      }
      return [];
    },
    headerStyle(): Record<string, string> {
      const styles: Record<string, string> = {};
      styles.backgroundColor = this.isScrolled ? getCssColorVar(this.vars?.backgroundColor as string) : 'transparent';
      const textColor = getCssColorVar(this.vars?.textColor as string);
      if (textColor) styles.color = textColor;
      styles.transition = 'background-color 0.3s ease, color 0.3s ease';
      if (this.menuVisible) {
        styles.backgroundColor = getCssColorVar(this.vars?.backgroundColor as string);
      }
      return styles;
    },
    menuStyle(): Record<string, string> {
      const styles: Record<string, string> = {};
      if (this.menuVisible) {
        styles.backgroundColor = '#ffffff';
      } else {
        const bgColor = getCssColorVar(this.vars?.backgroundColor as string);
        if (bgColor) styles.backgroundColor = bgColor;
      }
      const textColor = getCssColorVar(this.vars?.textColor as string);
      if (textColor) styles.color = textColor;
      return styles;
    },
  },
  methods: {
    toggleMenu(): void {
      this.menuVisible = !this.menuVisible;
      if (this.menuVisible) {
        document.body.classList.add('no-scroll');
        this.openSubMenu = null;
      } else {
        document.body.classList.remove('no-scroll');
      }
    },
    toggleSubMenu(index: number): void {
      this.openSubMenu = this.openSubMenu === index ? null : index;
    },
    handleMenuLinkClick(menuItem: MenuItem): void {
      if (!menuItem || menuItem.has_sub_menu || !menuItem.url) return;
      navigateTo(menuItem.url, {
        external: menuItem.is_open_new_tab || false,
        open: { target: menuItem.is_open_new_tab ? '_blank' : '_self' },
      });
      this.toggleMenu();
    },
    changeLanguage(lang: string): void {},
    handleScroll(): void {
      this.isScrolled = window.scrollY > this.scrollThreshold;
    },
    checkPopupOpen(): void {
      this.isPopupOpen = document.body.classList.contains('popup-open');
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    this.checkPopupOpen();
    const observer = new MutationObserver(() => this.checkPopupOpen());
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    this.$data.popupObserver = observer; // Store observer for cleanup
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.body.classList.remove('no-scroll');
    this.$data.popupObserver?.disconnect(); // Cleanup observer
  },
});
</script>

<style scoped>
.header__logo img.logo-image {
  max-height: 60px;
  width: auto;
  display: block;
  position: relative;
  z-index: 1050;
  transition: opacity 0.3s ease;
  opacity: 1 !important;
}
.header.-type-1 .header__logo {
  position: relative;
  margin-left: 0 !important;
  z-index: 0;
}
.header.-type-1 {
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1050;
}
.header.-type-1 .header__container {
  position: relative;
  z-index: 1060;
}
.menuNav__item a {
  padding: 0 !important;
}

.submenu__wrap {
  margin: 0;
  padding: 0;
}
.submenu__item {
  margin: 0;
  padding: 0;
}
.submenu__item h6 {
  color: #666666;
  margin: 0;
  padding: 0;
  line-height: 0;
  transition: color 0.3s ease;
}
.submenu__item h6:hover {
  color: var(--c-secondary) !important;
}
.submenu {
  display: none;
}
.submenu.is-active {
  display: block;
}
.menu.is-open {
  z-index: 1040;
}
.header.-type-1 .header__left {
  position: relative;
  z-index: 1060;
}
.header.-type-1.js-header .header__left .header__logo img.logo-image,
.menu.is-open + .header.-type-1 .header__logo img.logo-image {
  opacity: 1 !important;
}
.header.is-hidden {
  display: none !important;
}
</style>
